import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Star, PlusCircle, Radio, History, Settings, LogOut, Shield, Menu, X } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import useAuth from '../hooks/useAuth'
import useFeatureAccess from '../hooks/useFeatureAccess'
import Footer from './Footer'

const NAV_ALL = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard',   flag: null },
  { to: '/watchlist', icon: Star,            label: 'Watchlist',    flag: 'watchlist' },
  { to: '/create',    icon: PlusCircle,      label: 'Create Alert', flag: null },
  { to: '/signals',   icon: Radio,           label: 'Signals',      flag: 'signals' },
  { to: '/history',   icon: History,         label: 'History',      flag: null },
  { to: '/settings',  icon: Settings,        label: 'Settings',     flag: null },
]

export default function Layout({ children }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, profile, logout } = useAuth()
  const { can } = useFeatureAccess()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const NAV = NAV_ALL.filter(item => !item.flag || can(item.flag))

  const handleLogout = async () => {
    await logout()
    toast.success('Signed out')
    navigate('/login')
  }

  const planLabel = profile?.subscription_plan
    ? profile.subscription_plan.charAt(0).toUpperCase() + profile.subscription_plan.slice(1)
    : 'Free'

  return (
    <div className="flex h-screen bg-app overflow-hidden">

      {/* ── Sidebar (desktop) ───────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-56 bg-panel border-r border-border shrink-0">
        {/* Logo */}
        <div className="flex items-center justify-center px-3 h-[80px] border-b border-border shrink-0">
          <Link to="/" className="cursor-pointer transition-all duration-200 hover:brightness-[1.2]" style={{ animation: 'logo-glow 4s ease-in-out infinite' }}>
            <img src="/banner-transparent.png" alt="ALRTZ" className="w-[150px] h-auto object-contain" style={{ filter: 'drop-shadow(0 0 20px rgba(168,85,247,0.3)) drop-shadow(0 0 40px rgba(168,85,247,0.15))' }} />
          </Link>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, icon: Icon, label }) => {
            const active = pathname === to || pathname.startsWith(to + '/')
            return (
              <Link
                key={to}
                to={to}
                className={`nav-item flex items-center gap-3 px-3 py-2.5 rounded-none text-sm font-medium transition-all duration-150 ${
                  active
                    ? 'bg-brand/10 text-brand-light border border-brand/20 glow-border'
                    : 'text-white hover:text-ink border border-transparent'
                }`}
              >
                <Icon size={17} strokeWidth={active ? 2.5 : 2} />
                {label}
                {active && <div className="ml-auto w-1.5 h-1.5 rounded-none bg-brand animate-glow-pulse" />}
              </Link>
            )
          })}

          {/* Admin link — only for admin users */}
          {can('admin_panel') && (() => {
            const active = pathname === '/admin'
            return (
              <Link
                to="/admin"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-none text-sm font-medium transition-all duration-150 mt-2 border-t border-border pt-3 ${
                  active
                    ? 'bg-brand/10 text-brand-light'
                    : 'text-white hover:bg-surface-hover hover:text-ink'
                }`}
              >
                <Shield size={17} strokeWidth={active ? 2.5 : 2} />
                Admin
                {active && <div className="ml-auto w-1.5 h-1.5 rounded-none bg-brand animate-glow-pulse" />}
              </Link>
            )
          })()}
        </nav>

        {/* User footer */}
        <div className="border-t border-border px-2 py-3 space-y-0.5">
          <Link to="/settings" className="flex items-center gap-2.5 px-3 py-2 rounded-none hover:bg-surface-hover transition-all cursor-pointer">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-8 h-8 rounded-none object-cover shrink-0 border border-border" />
            ) : (
              <div className="w-8 h-8 rounded-none bg-gradient-to-br from-brand to-neon-pink flex items-center justify-center text-white font-bold text-xs shrink-0 glow-box">
                {user?.email?.[0]?.toUpperCase() ?? '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-ink text-xs font-medium truncate">{user?.user_metadata?.display_name || user?.email}</p>
              <p className="text-white text-[11px]">{planLabel} plan</p>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-none text-sm text-white hover:text-loss hover:bg-loss/5 transition-all"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Mobile topbar — glassmorphism */}
        <header className="md:hidden flex items-center justify-between px-4 h-14 glass-nav shrink-0 relative z-50">
          <Link to="/dashboard" className="transition-all duration-200 hover:brightness-[1.2]">
            <img src="/banner-transparent.png" alt="ALRTZ" className="w-[120px] h-auto object-contain" />
          </Link>
          <div className="flex items-center gap-2">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-7 h-7 rounded-none object-cover border border-border" />
            ) : (
              <div className="w-7 h-7 rounded-none bg-gradient-to-br from-brand to-neon-pink flex items-center justify-center text-white font-bold text-[10px]">
                {user?.email?.[0]?.toUpperCase() ?? '?'}
              </div>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 rounded-none text-white hover:text-brand-light transition-all"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </header>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-14 left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-brand/20 z-40 animate-slide-down">
            <nav className="px-4 py-3 space-y-1">
              {NAV.map(({ to, icon: Icon, label }) => {
                const active = pathname === to
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-none text-sm font-medium transition-all ${
                      active ? 'bg-brand/10 text-brand-light' : 'text-white hover:text-ink'
                    }`}
                  >
                    <Icon size={17} />
                    {label}
                  </Link>
                )
              })}
              {can('admin_panel') && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-none text-sm font-medium text-white hover:text-ink transition-all border-t border-border mt-1 pt-3"
                >
                  <Shield size={17} />
                  Admin
                </Link>
              )}
              <button
                onClick={() => { setMobileMenuOpen(false); handleLogout() }}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-none text-sm text-white hover:text-loss transition-all border-t border-border mt-1 pt-3"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </nav>
          </div>
        )}

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {children}
          <div className="md:hidden h-2" /> {/* Extra breathing room above mobile bottom nav */}
          <Footer />
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden flex items-center justify-around glass-nav py-1 shrink-0 border-t border-brand/10">
          {NAV.map(({ to, icon: Icon, label }) => {
            const active = pathname === to || pathname.startsWith(to + '/')
            return (
              <Link
                key={to}
                to={to}
                className={`flex flex-col items-center gap-0.5 px-4 py-2 transition-colors ${
                  active ? 'text-brand-light' : 'text-white'
                }`}
              >
                <Icon size={19} strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] font-medium">{label.split(' ')[0]}</span>
              </Link>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
